import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { useTable } from '@/components/data-table/core/context';

import type { ColumnOverlayProps } from './column-overlay.types';

interface OverlayPosition {
  columnId: string;
  left: number;
  width: number;
  top: number;
  height: number;
}

export const ColumnOverlay = ({
  columnIds,
  className,
  headerClassName,
  rowCount,
}: ColumnOverlayProps) => {
  const { table, updateOverlayColumns } = useTable();
  const [overlays, setOverlays] = useState<OverlayPosition[]>([]);

  const resolveClassName = (
    classNameValue: ColumnOverlayProps['className'] | ColumnOverlayProps['headerClassName'],
    columnId: string
  ): string | undefined => {
    return typeof classNameValue === 'function' ? classNameValue(columnId) : classNameValue;
  };

  // Update context with overlay columns and header style resolver.
  useLayoutEffect(() => {
    updateOverlayColumns(columnIds, headerClassName);

    // Cleanup: clear context when component unmounts.
    return () => {
      updateOverlayColumns([]);
    };
  }, [columnIds, headerClassName, updateOverlayColumns]);

  // Get table element from context
  const tableElement = table.options.meta?.tableRef?.current;

  // Calculate overlay positions from DOM
  const updateOverlays = useCallback(() => {
    if (!tableElement) return;

    // Determine row count: use prop if provided, otherwise use all rows
    const effectiveRowCount = rowCount !== undefined ? rowCount : table.getRowModel().rows.length;

    if (effectiveRowCount === 0) {
      setOverlays([]);
      return;
    }

    // Find the first row to get row height
    const tbody = tableElement.querySelector('tbody');
    const firstRow = tbody?.querySelector<HTMLElement>('tr[data-index="0"]');

    if (!firstRow) {
      setOverlays([]);
      return;
    }

    const rowHeight = firstRow.offsetHeight;
    const newOverlays: OverlayPosition[] = [];

    for (const columnId of columnIds) {
      const header = tableElement.querySelector<HTMLElement>(`[data-column-id="${columnId}"]`);

      if (header) {
        // Calculate total height: header + (row count × row height)
        const headerHeight = header.offsetHeight;
        const top = header.offsetTop;
        const height = headerHeight + effectiveRowCount * rowHeight;

        newOverlays.push({
          columnId,
          left: header.offsetLeft,
          width: header.offsetWidth,
          top,
          height,
        });
      }
    }

    setOverlays(newOverlays);
  }, [columnIds, tableElement, table, rowCount]);

  // ResizeObserver for column resizing
  useEffect(() => {
    if (!tableElement) return;

    const observer = new ResizeObserver(() => {
      updateOverlays();
    });

    observer.observe(tableElement);

    return () => {
      observer.disconnect();
    };
  }, [tableElement, updateOverlays]);

  // Track columnSizing and rowSelection changes from TanStack Table
  const columnSizing = table.getState().columnSizing;
  const rowSelection = table.getState().rowSelection;

  useEffect(() => {
    updateOverlays();
  }, [columnSizing, rowSelection, updateOverlays]);

  // Initial overlay calculation on mount
  useEffect(() => {
    updateOverlays();
  }, [updateOverlays]);

  // Return null if no overlays to render
  if (overlays.length === 0) {
    return null;
  }

  return (
    <>
      {overlays.map((overlay) => (
        <div
          key={overlay.columnId}
          data-testid={`column-highlight-${overlay.columnId}`}
          className={cn(
            'absolute pointer-events-none z-[10]',
            resolveClassName(className, overlay.columnId)
          )}
          style={{
            left: `${overlay.left}px`,
            width: `${overlay.width}px`,
            top: `${overlay.top}px`,
            height: `${overlay.height}px`,
          }}
        />
      ))}
    </>
  );
};

ColumnOverlay.displayName = 'DataTable.ColumnOverlay';
